import { Link } from 'react-router-dom'
import { Check, ClipboardCheck, CreditCard, ExternalLink, FileSignature, PackageCheck, Truck } from 'lucide-react'
import { C } from '../lib/tokens'

const ORDER_STAGES = [
  { key: 'questionnaire', label: 'Product form complete', icon: ClipboardCheck },
  { key: 'payment', label: 'Payment collected', icon: CreditCard },
  { key: 'provider_review', label: 'Sent for provider review', icon: FileSignature },
  { key: 'approved', label: 'Approved by provider', icon: ClipboardCheck },
  { key: 'received', label: 'Pharmacy Received', icon: PackageCheck },
  { key: 'processed', label: 'Order processed', icon: Check },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: PackageCheck },
]

const LAB_STAGES = [
  { key: 'paid', label: 'Paid', icon: CreditCard },
  { key: 'labs_ordered', label: 'Labs Ordered', icon: ClipboardCheck },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'waiting_collection', label: 'Waiting Collection', icon: PackageCheck },
  { key: 'results', label: 'Results Received', icon: PackageCheck },
]

const STATUS_STAGE = {
  draft: 0,
  pending: 0,
  pending_payment: 1,
  checkout_started: 1,
  paid_awaiting_provider: 2,
  provider_reviewing: 2,
  assigned_to_provider: 2,
  provider_assigned: 2,
  manual_placement_required: 2,
  manual_order_placed: 4,
  provider_approved: 3,
  approved: 3,
  received: 4,
  submitted: 4,
  submitted_to_pharmacy: 4,
  pharmacy_received: 4,
  pharmacy_order_received: 4,
  order_received: 4,
  order_received_by_pharmacy: 4,
  received_by_pharmacy: 4,
  processing: 5,
  pharmacy_fulfilling: 5,
  pharmacy_processing: 5,
  filling: 5,
  filled: 5,
  processed: 5,
  order_processed: 5,
  order_processed_by_pharmacy: 5,
  tracking_available: 6,
  tracking_number_available: 6,
  shipped: 6,
  order_shipped: 6,
  out_for_delivery: 6,
  delivered: 7,
  order_delivered: 7,
  delivered_to_patient: 7,
}

const LAB_STATUS_STAGE = {
  draft: 0,
  pending: 0,
  pending_payment: 0,
  payment_pending_licensee: 0,
  waiting_patient_login: 0,
  checkout_started: 1,
  lab_checkout_pending: 1,
  paid: 0,
  lab_paid: 0,
  payment_collected: 0,
  pending_intake: 0,
  intake_pending: 0,
  intake_complete: 1,
  paid_awaiting_provider: 0,
  provider_reviewing: 0,
  assigned_to_provider: 0,
  provider_assigned: 0,
  lab_routing_review: 0,
  lab_network_review: 0,
  access_ready_to_send: 1,
  ready_to_send_to_access: 1,
  manual_placement_required: 0,
  manual_order_placed: 1,
  elabs_setup_required: 0,
  elabs_order_failed: 0,
  paid_lab_routing_error: 0,
  requisition_created: 1,
  lab_requisition_created: 1,
  ordered: 1,
  labs_ordered: 1,
  scheduled: 3,
  collection_scheduled: 3,
  waiting_collection: 3,
  kit_shipped: 2,
  lab_shipped: 2,
  sample_collected: 3,
  collected: 3,
  in_lab: 3,
  processing: 3,
  results_processing: 3,
  access_order_sent: 2,
  sent_to_access: 2,
  results_pending: 3,
  completed: 4,
  resulted: 4,
  released: 4,
  final: 4,
  lab_results_received: 4,
  access_result_received: 4,
  result_received: 4,
  results_ready: 4,
  access_corrected_result_received: 4,
  corrected_result_received: 4,
  updated_results_ready: 4,
}

const STOPPED_STATUS = new Set(['provider_denied', 'provider_rejected', 'denied', 'cancelled', 'canceled', 'access_canceled', 'canceled_by_lab', 'do_not_send_canceled', 'do_not_send_/_canceled', 'deleted', 'refunded', 'failed'])

function isLabOrder(order = {}) {
  return order.source === 'local_lab_order' || order.order_type === 'lab_order' || order.product?.category === 'lab'
}

function stagesForOrder(order = {}) {
  return isLabOrder(order) ? LAB_STAGES : ORDER_STAGES
}

function normalizedStatus(order = {}) {
  return String(
    order.status ||
    order.order_status ||
    order.fulfillment_status ||
    order.pharmacy_status ||
    order.activepharm_status ||
    order.active_pharm_status ||
    order.account_status ||
    order.lab_status ||
    ''
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

function trackingNumber(order = {}) {
  return order.pharmacy_tracking_number || order.tracking_number || order.trackingNumber || order.shipment_tracking_number || ''
}

function carrierName(order = {}) {
  return order.pharmacy_tracking_carrier || order.tracking_carrier || order.carrier_name || order.carrier || order.shipping_carrier || ''
}

function exactTrackingUrl(order = {}) {
  return order.pharmacy_tracking_url || order.tracking_url || order.trackingUrl || order.shipment_tracking_url || ''
}

function trackingUrl(order = {}) {
  const tracking = String(trackingNumber(order)).trim()
  if (!tracking) return ''
  const exact = String(exactTrackingUrl(order)).trim()
  if (exact) return exact

  const encoded = encodeURIComponent(tracking)
  const carrier = String(carrierName(order)).toLowerCase()
  if (carrier.includes('ups')) return `https://www.ups.com/track?tracknum=${encoded}`
  if (carrier.includes('fedex') || carrier.includes('fed ex')) return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`
  if (carrier.includes('usps') || carrier.includes('postal')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`
  if (carrier.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encoded}`
  if (carrier.includes('ontrac')) return `https://www.ontrac.com/tracking/?number=${encoded}`
  if (carrier.includes('lasership') || carrier.includes('laser ship')) return `https://www.lasership.com/track/${encoded}`
  return `https://www.google.com/search?q=${encoded}%20tracking`
}

function productNameForOrder(order = {}) {
  return order.lab_panel?.name ||
    order.product?.display_name ||
    order.product_display_name ||
    order.product_full_name ||
    order.medication_name ||
    order.product?.medication_name ||
    'Order'
}

function currentStage(order = {}) {
  const status = normalizedStatus(order)
  if (isLabOrder(order)) {
    if (STOPPED_STATUS.has(status)) return 1
    return LAB_STATUS_STAGE[status] ?? 0
  }
  if (STOPPED_STATUS.has(status)) {
    if (['provider_denied', 'provider_rejected', 'denied'].includes(status)) return 2
    if (status === 'refunded') return 1
    return 0
  }
  if (status === 'delivered') return 7
  if (trackingNumber(order)) return Math.max(STATUS_STAGE[status] ?? 0, 6)
  return STATUS_STAGE[status] ?? 0
}

function stageTone(index, activeIndex, stopped) {
  if (stopped) return index <= activeIndex ? 'stopped' : 'future'
  if (index < activeIndex) return 'done'
  if (index === activeIndex) return 'current'
  return 'future'
}

function metaValue(order = {}, ...keys) {
  const metadata = order.metadata && typeof order.metadata === 'object' ? order.metadata : {}
  const accessLabsFlow = metadata.kis_rx_access_labs_flow && typeof metadata.kis_rx_access_labs_flow === 'object'
    ? metadata.kis_rx_access_labs_flow
    : {}
  for (const key of keys) {
    if (order[key]) return order[key]
    if (metadata[key]) return metadata[key]
    if (accessLabsFlow[key]) return accessLabsFlow[key]
  }
  return ''
}

function firstDateValue(...values) {
  for (const value of values) {
    if (!value) continue
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) return value
  }
  return ''
}

function displayStageDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)
  return `${month}/${day}/${year}`
}

function displayStatusDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function pharmacyReceivedAt(order = {}) {
  return firstDateValue(
    metaValue(order, 'pharmacy_received_at', 'pharmacyReceivedAt', 'received_at'),
    order.pharmacy_received_at,
    order.received_at
  )
}

function completedDateForStage(order = {}, stageKey, index, activeIndex) {
  if (index > activeIndex) return ''
  switch (stageKey) {
    case 'questionnaire':
      return firstDateValue(
        metaValue(order, 'questionnaire_completed_at', 'intake_completed_at', 'intakeCompletedAt'),
        order.created_at
      )
    case 'payment':
    case 'paid':
      return firstDateValue(
        metaValue(order, 'payment_collected_at', 'paid_at', 'paidAt', 'checkout_completed_at', 'checkoutCompletedAt'),
        order.updated_at,
        order.created_at
      )
    case 'provider_review':
    case 'routing':
      return firstDateValue(
        metaValue(order, 'provider_review_sent_at', 'providerReviewSentAt', 'provider_review_started_at', 'providerReviewStartedAt'),
        metaValue(order, 'payment_collected_at', 'paid_at', 'checkout_completed_at'),
        order.updated_at,
        order.created_at
      )
    case 'approved':
      return firstDateValue(
        metaValue(order, 'provider_reviewed_at', 'provider_approved_at', 'approved_at', 'providerReviewedAt', 'date_written', 'effective_date'),
        order.updated_at,
        order.created_at
      )
    case 'received':
      return firstDateValue(
        metaValue(order, 'pharmacy_received_at', 'pharmacyReceivedAt', 'received_at', 'submitted_at', 'date_written', 'effective_date'),
        metaValue(order, 'provider_reviewed_at', 'approved_at'),
        order.updated_at,
        order.created_at
      )
    case 'processed':
      return firstDateValue(
        metaValue(order, 'pharmacy_processed_at', 'pharmacyProcessedAt', 'processed_at', 'filled_at'),
        order.updated_at,
        order.created_at
      )
    case 'shipped':
      return firstDateValue(
        metaValue(order, 'pharmacy_shipped_at', 'pharmacyShippedAt', 'shipped_at', 'shipment_shipped_at', 'kit_shipped_at', 'collection_scheduled_at'),
        order.updated_at,
        order.created_at
      )
    case 'delivered':
      return firstDateValue(
        metaValue(order, 'pharmacy_delivered_at', 'pharmacyDeliveredAt', 'delivered_at'),
        order.updated_at,
        order.created_at
      )
    case 'waiting_collection':
      return firstDateValue(metaValue(order, 'waiting_collection_at', 'collection_scheduled_at', 'sample_collected_at', 'collected_at'), order.updated_at, order.created_at)
    case 'labs_ordered':
      return firstDateValue(metaValue(order, 'labs_ordered_at', 'requisition_created_at', 'lab_requisition_created_at'), order.updated_at, order.created_at)
    case 'intake':
      return firstDateValue(metaValue(order, 'intake_completed_at', 'intakeCompletedAt'), order.created_at)
    case 'requisition':
      return firstDateValue(metaValue(order, 'requisition_created_at', 'lab_requisition_created_at'), order.updated_at, order.created_at)
    case 'sample':
      return firstDateValue(metaValue(order, 'sample_collected_at', 'collected_at'), order.updated_at, order.created_at)
    case 'processing':
      return firstDateValue(metaValue(order, 'results_processing_at', 'processing_started_at'), order.updated_at, order.created_at)
    case 'results':
      return firstDateValue(metaValue(order, 'latest_result_received_at', 'resulted_at', 'results_ready_at'), order.updated_at, order.created_at)
    default:
      return firstDateValue(order.updated_at, order.created_at)
  }
}

function statusCopy(order = {}) {
  const status = normalizedStatus(order)
  if (isLabOrder(order)) {
    if (['provider_denied', 'provider_rejected', 'denied'].includes(status)) return 'A provider was unable to approve this lab request.'
    if (status === 'cancelled' || status === 'canceled') return 'This lab order was cancelled.'
    if (status === 'deleted') return 'This lab order was deleted.'
    if (status === 'refunded') return 'This lab order was refunded.'
    if (status === 'payment_pending_licensee') return 'Your lab order has been received from your coach.'
    if (status === 'waiting_patient_login') return 'Your lab order is waiting for portal setup.'
    if (status === 'paid') return 'Your lab order has been received.'
    if (status === 'labs_ordered') return 'Your labs have been ordered.'
    if (status === 'lab_shipped') return 'Your labs have shipped.'
    if (status === 'waiting_collection') return 'Your order is waiting for collection.'
    if (status === 'lab_results_received') return 'Results are ready for review.'
    if (status === 'manual_placement_required') return 'Your lab order is being placed with the lab partner.'
    if (status === 'manual_order_placed') return 'Your lab order has been placed with the lab partner.'
    if (['access_ready_to_send', 'ready_to_send_to_access'].includes(status)) return 'Your lab order is being prepared.'
    if (['access_order_sent', 'sent_to_access', 'results_pending'].includes(status)) return 'Your lab order has been submitted. Results are pending.'
    if (['access_result_received', 'result_received', 'results_ready'].includes(status)) return 'Results are ready for review.'
    if (['access_corrected_result_received', 'corrected_result_received', 'updated_results_ready'].includes(status)) return 'Updated results are ready for review.'
    if (['access_canceled', 'canceled_by_lab', 'do_not_send_canceled', 'do_not_send_/_canceled'].includes(status)) return 'This lab order was canceled.'
    if (status === 'elabs_setup_required' || status === 'elabs_order_failed' || status === 'paid_lab_routing_error') return 'KIS Rx is reviewing this lab order and will follow up if anything else is needed.'
    if (status === 'failed') return 'This lab order needs support review.'
    if (['completed', 'resulted', 'released', 'final'].includes(status)) return 'Results are ready for review.'
    if (['processing', 'results_processing', 'in_lab'].includes(status)) return 'Your sample is being processed.'
    if (['sample_collected', 'collected'].includes(status)) return 'Sample collected. Results processing is next.'
    if (['collection_scheduled', 'scheduled', 'kit_shipped'].includes(status)) return 'Collection instructions are active.'
    if (['requisition_created', 'lab_requisition_created', 'ordered'].includes(status)) return 'The lab requisition has been created.'
    if (['paid_awaiting_provider', 'provider_reviewing', 'lab_routing_review', 'lab_network_review'].includes(status)) return 'Your lab order is being reviewed.'
    if (['lab_paid', 'payment_collected', 'pending_intake', 'intake_pending'].includes(status)) return 'Panel purchased. Complete intake to keep the lab order moving.'
    if (status === 'intake_complete') return 'Intake complete. Lab order review is next.'
    if (['checkout_started', 'lab_checkout_pending'].includes(status)) return 'Checkout is ready for this lab request.'
    return 'Panel selected. Purchase and intake are next.'
  }
  if (['provider_denied', 'provider_rejected', 'denied'].includes(status)) return 'Your provider was unable to approve this request.'
  if (status === 'cancelled' || status === 'canceled') return 'This order was cancelled.'
  if (status === 'deleted') return 'This order was deleted.'
  if (status === 'refunded') return 'This order was refunded.'
  if (status === 'manual_placement_required') return 'Your order is being placed with the fulfillment partner.'
  if (status === 'manual_order_placed') return 'Your order has been placed with the fulfillment partner.'
  if (status === 'failed') return 'This order needs support review.'
  if (status === 'delivered') return 'Delivered to patient.'
  if (trackingNumber(order)) return 'Tracking is available.'
  if (['pending_payment', 'checkout_started', 'pending_checkout'].includes(status)) return 'Order started. Message support if you need help finishing checkout.'
  if (['paid_awaiting_provider', 'provider_reviewing', 'assigned_to_provider', 'provider_assigned'].includes(status)) {
    return 'Order received. The care team will send an intake form for provider review if required.'
  }
  if (['provider_approved', 'approved'].includes(status)) return 'Provider approved. Pharmacy review is next.'
  if (['received', 'submitted', 'submitted_to_pharmacy', 'pharmacy_received', 'pharmacy_order_received', 'order_received', 'order_received_by_pharmacy', 'received_by_pharmacy'].includes(status)) {
    const receivedAt = displayStatusDateTime(pharmacyReceivedAt(order))
    return receivedAt
      ? `Received by pharmacy on ${receivedAt}. This page will update when processing begins.`
      : 'Pharmacy received the prescription. This page will update when processing begins.'
  }
  if (['processing', 'pharmacy_fulfilling', 'pharmacy_processing', 'filling', 'filled', 'processed', 'order_processed', 'order_processed_by_pharmacy'].includes(status)) {
    return 'Order is being processed.'
  }
  return ORDER_STAGES[currentStage(order)]?.label || 'Order in progress.'
}

function productIntakeHref(order = {}) {
  const productId = order.product_id || order.productId || order.product?.id || order.portal_product_id || order.portalProductId || ''
  return productId ? `/shop?product=${encodeURIComponent(productId)}` : '/shop'
}

function directActionHref(order = {}, stageKey, index, activeIndex, stopped) {
  if (stopped || index !== activeIndex) return ''
  const status = normalizedStatus(order)
  const checkoutUrl = metaValue(order, 'checkout_url', 'checkoutUrl', 'stripe_checkout_url', 'stripeCheckoutUrl', 'payment_url', 'paymentUrl')
  if (isLabOrder(order)) {
    if (stageKey === 'paid' && ['draft', 'pending', 'pending_payment', 'checkout_started', 'lab_checkout_pending'].includes(status)) {
      return checkoutUrl || '/labs'
    }
    if (['waiting_collection', 'results'].includes(stageKey)) return '/labs'
    return ''
  }
  if (stageKey === 'questionnaire' && ['draft', 'pending'].includes(status)) return productIntakeHref(order)
  if (stageKey === 'payment' && ['pending_payment', 'checkout_started'].includes(status)) return checkoutUrl || productIntakeHref(order)
  if (stageKey === 'shipped' && trackingNumber(order)) return trackingUrl(order)
  return ''
}

function activeStageIsCompleted(order = {}, stageKey) {
  const status = normalizedStatus(order)
  if (isLabOrder(order)) {
    return stageKey === 'results' && ['completed', 'resulted', 'released', 'final', 'lab_results_received', 'access_result_received', 'result_received', 'results_ready', 'access_corrected_result_received', 'corrected_result_received', 'updated_results_ready'].includes(status)
  }
  if (stageKey === 'approved' && ['provider_approved', 'approved'].includes(status)) return true
  if (stageKey === 'received' && ['received', 'submitted', 'submitted_to_pharmacy', 'pharmacy_received', 'pharmacy_order_received', 'order_received', 'order_received_by_pharmacy', 'received_by_pharmacy'].includes(status)) return true
  return stageKey === 'delivered' && ['delivered', 'order_delivered', 'delivered_to_patient'].includes(status)
}

function ActionCircle({ href, children, title, style }) {
  if (!href) return <span style={style}>{children}</span>
  const sharedStyle = {
    ...style,
    textDecoration: 'none',
    cursor: 'pointer',
  }
  if (/^https?:\/\//i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={sharedStyle} title={title}>
        {children}
      </a>
    )
  }
  return (
    <Link to={href} style={sharedStyle} title={title}>
      {children}
    </Link>
  )
}

function Step({ stage, index, activeIndex, stopped, compact, completedAt, completed, actionHref }) {
  const Icon = stage.icon
  const tone = stageTone(index, activeIndex, stopped)
  const isDone = tone === 'done' || completed
  const isCurrent = tone === 'current' && !isDone
  const dateLabel = displayStageDate(completedAt)
  const colors = ({
    done: { bg: 'linear-gradient(180deg, #05C875 0%, #00A85F 100%)', border: C.green, fg: '#fff', text: C.text },
    current: { bg: stopped ? '#FFF2F2' : 'linear-gradient(180deg, #F4FFF8 0%, #E6FAF0 100%)', border: stopped ? '#F5B7B1' : C.green, fg: stopped ? '#A6201B' : C.green, text: C.text },
    stopped: { bg: '#FDEAEA', border: '#F5B7B1', fg: '#A6201B', text: '#A6201B' },
    future: { bg: '#fff', border: C.borderStrong, fg: C.textTertiary, text: C.textTertiary },
  })[isDone ? 'done' : tone]
  const detailLabel = dateLabel || (isCurrent ? 'In progress' : (!stopped && index > activeIndex ? 'Waiting' : ''))

  return (
    <div className={`order-progress-step order-progress-step-${isDone ? 'done' : tone}`} style={{
      display: 'grid',
      gridTemplateRows: '42px auto auto',
      justifyItems: 'center',
      gap: 6,
      minWidth: compact ? 72 : 92,
      flex: '1 1 0',
      position: 'relative',
      zIndex: 1,
    }}>
      <ActionCircle
        href={actionHref}
        title={actionHref ? `Open ${stage.label}` : undefined}
        style={{
          width: 42,
          height: 42,
          borderRadius: 21,
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.fg,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isCurrent || isDone ? C.greenShadow : '0 6px 14px rgba(17,24,39,0.045)',
        }}
      >
        {isDone ? <Check size={18} strokeWidth={2.5} /> : <Icon size={18} strokeWidth={2.1} />}
      </ActionCircle>
      <span style={{
        color: colors.text,
        fontSize: compact ? 11 : 12,
        fontWeight: isCurrent ? 760 : 650,
        lineHeight: 1.25,
        textAlign: 'center',
        maxWidth: 100,
      }}>
        {stage.label}
      </span>
      <span style={{
        color: C.textTertiary,
        fontSize: compact ? 10.5 : 11,
        fontWeight: 650,
        lineHeight: 1.15,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        visibility: detailLabel ? 'visible' : 'hidden',
      }}>
        {detailLabel || 'Waiting'}
      </span>
    </div>
  )
}

export default function OrderProgressTracker({ order, compact = false }) {
  const activeIndex = currentStage(order)
  const status = normalizedStatus(order)
  const stopped = STOPPED_STATUS.has(status)
  const tracking = trackingNumber(order)
  const carrier = carrierName(order)
  const url = trackingUrl(order)
  const stages = stagesForOrder(order)
  const progressTitle = `${productNameForOrder(order)} Order Progress`
  const activeStageCompleted = activeStageIsCompleted(order, stages[activeIndex]?.key)
  const progressIndex = activeStageCompleted ? activeIndex + 1 : activeIndex
  const stageStates = stages.map((stage, index) => {
    const completed = index < activeIndex || (index === activeIndex && activeStageIsCompleted(order, stage.key))
    return {
      stage,
      index,
      completed,
      completedAt: completed ? completedDateForStage(order, stage.key, index, activeIndex) : '',
    }
  })

  return (
    <div className="order-progress-tracker" style={{
      border: `1px solid ${C.border}`,
      borderRadius: compact ? 18 : 20,
      background: compact ? 'linear-gradient(180deg, #FBFCFE 0%, #F7F8FA 100%)' : 'linear-gradient(180deg, #FFFFFF 0%, #F7FBFF 100%)',
      padding: compact ? 14 : 18,
      display: 'grid',
      gap: compact ? 12 : 16,
      overflow: 'hidden',
      boxShadow: compact ? 'inset 0 1px 0 rgba(255,255,255,0.82)' : C.shadowSoft,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: compact ? 12.5 : 13.5, fontWeight: 780, color: C.text }}>{progressTitle}</div>
          <div style={{ fontSize: compact ? 12 : 12.5, color: C.textSecondary, marginTop: 3 }}>{statusCopy(order)}</div>
        </div>
        {tracking && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 10px',
              borderRadius: 999,
              border: `1px solid ${C.green}`,
              background: 'linear-gradient(180deg, #EAFBF2 0%, #DFF8EC 100%)',
              color: C.green,
              textDecoration: 'none',
              fontSize: 12.5,
              fontWeight: 760,
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              minWidth: 0,
            }}
            title={`Track ${tracking}${carrier ? ` with ${carrier}` : ''}`}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {carrier || 'Track package'} {tracking}
            </span>
            <ExternalLink size={13} strokeWidth={2.2} />
          </a>
        )}
      </div>

      <div style={{
        position: 'relative',
        display: 'flex',
        gap: compact ? 8 : 10,
        alignItems: 'start',
        overflowX: 'auto',
        paddingBottom: 2,
      }}>
        <div style={{
          position: 'absolute',
          top: 20,
          left: 28,
          right: 28,
          height: 3,
          borderRadius: 999,
          background: '#E5E8EE',
          overflow: 'hidden',
          zIndex: 0,
          pointerEvents: 'none',
        }}>
          <div style={{
            width: `${Math.max(0, Math.min(100, (progressIndex / (stages.length - 1)) * 100))}%`,
            height: '100%',
            background: stopped ? '#F5B7B1' : 'linear-gradient(90deg, #00BF6D, #30D158)',
          }} />
        </div>
        {stageStates.map(({ stage, index, completed, completedAt }) => (
          <Step
            key={stage.key}
            stage={stage}
            index={index}
            activeIndex={activeIndex}
            stopped={stopped}
            compact={compact}
            completed={completed}
            completedAt={completedAt}
            actionHref={directActionHref(order, stage.key, index, activeIndex, stopped)}
          />
        ))}
      </div>
    </div>
  )
}
